import { API_KEY, CLIENT_APP, CLIENT_NAME, IDENTITY_SERVICE_BASE_URL } from './clientConfig';
import { getUser } from './authService';

const IDENTITY_API_BASE = IDENTITY_SERVICE_BASE_URL;

function buildHeaders(token: string) {
  const clientId = localStorage.getItem('olitask_client_id') || '';
  const actorId = getUser()?.id || '';
  const bearer = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: bearer,
    'O-CLIENT-APP': CLIENT_APP,
    'O-API-KEY': API_KEY,
    'O-CLIENT-NAME': CLIENT_NAME,
  };

  if (clientId) headers['O-CLIENT-ID'] = clientId;
  if (actorId) headers['O-ACTOR-ID'] = actorId;
  return headers;
}

function normalizeOtpResponse(data: any, label: string) {
  const otpToken =
    data?.otpToken ||
    data?.otp_token ||
    data?.token ||
    data?.verificationToken ||
    data?.verification_token ||
    data?.verificationSessionId;

  if (!otpToken) throw new Error(`${label} verification did not start. Please try again.`);
  return { ...data, otpToken };
}

export async function getUserVerificationStatus(token: string) {
  const response = await fetch(`${IDENTITY_API_BASE}/api/v1/auth/users/me/verification-status`, {
    method: 'GET',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to fetch verification status');
  }

  return response.json();
}

export async function startEmailVerification(token: string) {
  const response = await fetch(`${IDENTITY_API_BASE}/api/v1/auth/verification/email/start`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to start email verification');
  }

  const data = await response.json();
  return normalizeOtpResponse(data, 'Email');
}

export async function verifyEmail(otpToken: string, code: string, token: string) {
  const response = await fetch(`${IDENTITY_API_BASE}/api/v1/auth/verification/email/verify`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ otpToken, code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to verify email');
  }

  const data = await response.json();
  if (data?.success === false) throw new Error(data?.message || 'Failed to verify email');
  return data;
}

export async function startPhoneVerification(token: string, phone?: string) {
  const response = await fetch(`${IDENTITY_API_BASE}/api/v1/auth/verification/phone/start`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(phone ? { phone } : {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to start phone verification');
  }

  const data = await response.json();
  return normalizeOtpResponse(data, 'Phone');
}

export async function verifyPhone(otpToken: string, code: string, token: string) {
  const response = await fetch(`${IDENTITY_API_BASE}/api/v1/auth/verification/phone/verify`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ otpToken, code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to verify phone');
  }

  const data = await response.json();
  if (data?.success === false) throw new Error(data?.message || 'Failed to verify phone');
  return data;
}
